import { useState, useEffect, useMemo } from "react";
import { getPhotoUrl } from '@/utils/photoUrl';
import { getAvatarColor, getInitials } from '@/utils/avatar';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { PageLayout } from "@/components/layout/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { NotificationCard } from "@/components/NotificationToggle";
import { 
  Users, 
  ClipboardList, 
  TrendingUp, 
  Target,
  Trophy,
  Medal,
  Crown,
  AlertTriangle,
  ArrowRight,
  Zap,
  Calendar,
  Swords,
  Clock
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Area, AreaChart, ResponsiveContainer } from "recharts";

const POSITIONS = ["Levantador", "Ponteiro", "Oposto", "Central", "Líbero"];

export default function Dashboard({ evaluator, onLogout }) {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [averages, setAverages] = useState([]);
  const [evaluationsCount, setEvaluationsCount] = useState(0);
  const [playersNeedingEval, setPlayersNeedingEval] = useState({ total: 0, players: [] });
  const [dashExtra, setDashExtra] = useState({ recent_games: [], upcoming_events: [], evaluation_trend: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [positionFilter, setPositionFilter] = useState("all");
  const [showAlert, setShowAlert] = useState(true);

  const transformAverageData = (data) => {
    return data.map(p => {
      const avgValues = Object.values(p.averages || {}).filter(v => v > 0);
      const calculatedAvg = avgValues.length > 0 ? avgValues.reduce((a, b) => a + b, 0) / avgValues.length : 0;
      return {
        id: p.player_id,
        name: p.player_name,
        first_name: p.player_first_name,
        last_name: p.player_last_name,
        position: p.player_position,
        yankee: p.player_yankee,
        photo_url: p.player_photo_url,
        average: calculatedAvg,
        evaluations_count: p.total_evaluations
      };
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [playersRes, averagesRes, evaluationsRes, needingEvalRes, extraRes] = await Promise.all([
          axios.get(`${API}/players`),
          axios.get(`${API}/players/all/averages`),
          axios.get(`${API}/evaluations`),
          axios.get(`${API}/players/needs-evaluation`),
          axios.get(`${API}/statistics/dashboard-extra`),
        ]);
        setPlayers(playersRes.data);
        setAverages(transformAverageData(averagesRes.data));
        setEvaluationsCount(evaluationsRes.data.length);
        setPlayersNeedingEval(needingEvalRes.data);
        setDashExtra(extraRes.data);
      } catch (error) {
        toast.error("Erro ao carregar dados");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getPlayerFullName = (p) => p.first_name ? `${p.first_name} ${p.last_name}` : (p.player_name || p.name);
  
  const getRankIcon = (index) => {
    if (index === 0) return <Crown className="h-5 w-5 text-yellow-400" />;
    if (index === 1) return <Medal className="h-5 w-5 text-slate-300" />;
    if (index === 2) return <Medal className="h-5 w-5 text-amber-600" />;
    return <span className="text-muted-foreground font-bold text-sm">{index + 1}</span>;
  };

  const globalAverage = averages.length > 0 
    ? (averages.reduce((acc, p) => acc + p.average, 0) / averages.length).toFixed(1)
    : "0.0";

  const rankedAverages = useMemo(() => [...averages].sort((a, b) => b.average - a.average), [averages]);
  
  const filteredRankedAverages = positionFilter === "all" 
    ? rankedAverages.slice(0, 10)
    : rankedAverages.filter(p => p.position === positionFilter).slice(0, 10);

  const totalTrendEvals = dashExtra.evaluation_trend.reduce((s, w) => s + w.count, 0);

  const formatGameDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}`;
  };

  return (
    <PageLayout evaluator={evaluator} onLogout={onLogout}>
      {/* Alert Banner */}
      {showAlert && playersNeedingEval.total > 0 && (
        <div className="mb-4 md:mb-6 bg-[hsl(var(--highlight))]/10 border border-[hsl(var(--highlight))]/30 rounded-xl p-3 md:p-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[hsl(var(--highlight))]/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-[hsl(var(--highlight))]" />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-foreground text-sm md:text-base">
                {playersNeedingEval.total} jogador{playersNeedingEval.total > 1 ? 'es' : ''} aguardando
              </p>
              <p className="text-xs md:text-sm text-muted-foreground truncate">
                {playersNeedingEval.players?.slice(0, 3).map(p => getPlayerFullName(p)).join(", ")}
                {playersNeedingEval.players?.length > 3 && ` +${playersNeedingEval.players.length - 3}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            <button
              onClick={() => navigate('/evaluate')}
              className="px-3 py-1.5 md:px-4 md:py-2 bg-[hsl(var(--highlight))] text-[hsl(var(--highlight-foreground))] font-bold rounded-lg hover:opacity-90 transition-all text-xs md:text-sm"
              data-testid="alert-evaluate-btn"
            >
              Avaliar
            </button>
            <button
              onClick={() => setShowAlert(false)}
              className="p-1 md:p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <span className="text-xs">&#10005;</span>
            </button>
          </div>
        </div>
      )}

      {/* Notification Card - Only for admins */}
      {evaluator?.role === 'admin' && (
        <div className="mb-4 md:mb-6">
          <NotificationCard evaluator={evaluator} />
        </div>
      )}

      {/* Stats Grid - 2 cols mobile, 4 cols desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        {/* Players Count */}
        <Card 
          className="bg-card/80 border border-border rounded-xl hover:border-primary/50 transition-all duration-200 group cursor-pointer"
          onClick={() => navigate('/players')}
          data-testid="stat-players"
        >
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] md:text-xs uppercase tracking-widest text-muted-foreground font-bold">Jogadores</p>
                <p className="text-2xl md:text-4xl font-black text-foreground mt-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {isLoading ? "..." : players.length}
                </p>
              </div>
              <div className="w-9 h-9 md:w-12 md:h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                <Users className="h-4 w-4 md:h-6 md:w-6 text-primary" />
              </div>
            </div>
            <p className="mt-1.5 md:mt-3 text-[10px] md:text-xs text-muted-foreground group-hover:text-primary transition-colors flex items-center gap-1">
              Ver todos <ArrowRight className="h-3 w-3" />
            </p>
          </CardContent>
        </Card>

        {/* Evaluations Count with mini trend */}
        <Card 
          className="bg-card/80 border border-border rounded-xl hover:border-[hsl(var(--highlight))]/50 transition-all duration-200 group cursor-pointer"
          onClick={() => navigate('/evaluate')}
          data-testid="stat-evaluations"
        >
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] md:text-xs uppercase tracking-widest text-muted-foreground font-bold">Avaliações</p>
                <p className="text-2xl md:text-4xl font-black text-foreground mt-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {isLoading ? "..." : evaluationsCount}
                </p>
              </div>
              <div className="w-9 h-9 md:w-12 md:h-12 rounded-xl bg-[hsl(var(--highlight))]/20 flex items-center justify-center">
                <ClipboardList className="h-4 w-4 md:h-6 md:w-6 text-[hsl(var(--highlight))]" />
              </div>
            </div>
            {/* Mini sparkline */}
            {dashExtra.evaluation_trend.length > 0 && (
              <div className="h-6 md:h-8 mt-1 -mx-1">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dashExtra.evaluation_trend}>
                    <defs>
                      <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--highlight))" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="hsl(var(--highlight))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="hsl(var(--highlight))"
                      strokeWidth={1.5}
                      fill="url(#trendFill)"
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Global Average */}
        <Card 
          className="bg-card/80 border border-border rounded-xl hover:border-green-500/50 transition-all duration-200 group cursor-pointer"
          onClick={() => navigate('/statistics')}
          data-testid="stat-average"
        >
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] md:text-xs uppercase tracking-widest text-muted-foreground font-bold">Média Global</p>
                <p className="text-2xl md:text-4xl font-black text-foreground mt-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {isLoading ? "..." : globalAverage}
                </p>
              </div>
              <div className="w-9 h-9 md:w-12 md:h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 md:h-6 md:w-6 text-green-400" />
              </div>
            </div>
            <p className="mt-1.5 md:mt-3 text-[10px] md:text-xs text-muted-foreground group-hover:text-green-400 transition-colors flex items-center gap-1">
              Estatísticas <ArrowRight className="h-3 w-3" />
            </p>
          </CardContent>
        </Card>

        {/* Quick Action */}
        <Card className="bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/30 rounded-xl" data-testid="stat-quick-actions">
          <CardContent className="p-3 md:p-5 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] md:text-xs uppercase tracking-widest text-muted-foreground font-bold">Ação Rápida</p>
              <Zap className="h-3.5 w-3.5 md:h-4 md:w-4 text-primary" />
            </div>
            {['admin', 'tecnico'].includes(evaluator?.role) && (
              <button 
                onClick={() => navigate('/gameday')}
                className="w-full py-1.5 md:py-2.5 bg-primary text-primary-foreground font-bold uppercase tracking-wider rounded-lg hover:bg-primary/90 transition-all text-[10px] md:text-sm"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Gerar Times
              </button>
            )}
            <button 
              onClick={() => navigate('/evaluate')}
              className="w-full py-1.5 md:py-2.5 bg-[hsl(var(--highlight))] text-[hsl(var(--highlight-foreground))] font-bold uppercase tracking-wider rounded-lg hover:opacity-90 transition-all text-[10px] md:text-sm"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Avaliar
            </button>
          </CardContent>
        </Card>
      </div>

      {/* Últimos Jogos + Próximas Partidas row */}
      {(dashExtra.recent_games.length > 0 || dashExtra.upcoming_events.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
          {/* Últimos Resultados */}
          {dashExtra.recent_games.length > 0 && (
            <Card className="bg-card/80 border border-border rounded-xl" data-testid="recent-games">
              <CardHeader className="pb-2 px-4 pt-4 md:px-5">
                <CardTitle className="text-sm md:text-base font-bold uppercase tracking-tight flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  <Swords className="h-4 w-4 text-primary" />
                  Últimos Jogos
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 md:px-5 md:pb-5">
                <div className="space-y-2">
                  {dashExtra.recent_games.slice(0, 3).map((game) => (
                    <div
                      key={game.id}
                      className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate('/gameday')}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                          <Swords className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{formatGameDate(game.date)}</p>
                          <p className="text-[10px] text-muted-foreground">{game.num_players} jogadores</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-[10px]">
                        {game.num_teams} times
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Próximas Partidas */}
          <Card className="bg-card/80 border border-border rounded-xl" data-testid="upcoming-events">
            <CardHeader className="pb-2 px-4 pt-4 md:px-5">
              <CardTitle className="text-sm md:text-base font-bold uppercase tracking-tight flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                <Calendar className="h-4 w-4 text-[hsl(var(--highlight))]" />
                Próximas Partidas
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 md:px-5 md:pb-5">
              {dashExtra.upcoming_events.length > 0 ? (
                <div className="space-y-2">
                  {dashExtra.upcoming_events.slice(0, 3).map((event) => (
                    <div
                      key={event.id}
                      className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate('/calendar')}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-[hsl(var(--highlight))]/20 flex items-center justify-center">
                          <Clock className="h-3.5 w-3.5 text-[hsl(var(--highlight))]" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{event.title}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {formatGameDate(event.date)} {event.time && `às ${event.time}`}
                          </p>
                        </div>
                      </div>
                      {event.location && (
                        <span className="text-[10px] text-muted-foreground truncate max-w-20">{event.location}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 md:py-6 text-muted-foreground">
                  <Calendar className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-xs">Nenhum evento agendado</p>
                  <button
                    onClick={() => navigate('/calendar')}
                    className="text-xs text-primary hover:underline mt-1"
                  >
                    Ir ao Calendário
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Grid - Ranking + Pendentes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
        {/* Rankings - 2 columns */}
        <Card className="lg:col-span-2 bg-card/80 border border-border rounded-xl">
          <CardHeader className="border-b border-border pb-3 px-4 md:px-5">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm md:text-lg font-bold uppercase tracking-tight flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                <Trophy className="h-4 w-4 text-[hsl(var(--highlight))]" />
                Top 10 Ranking
              </CardTitle>
              <Select value={positionFilter} onValueChange={setPositionFilter}>
                <SelectTrigger className="w-28 md:w-36 bg-muted/50 border-border h-8 text-xs md:text-sm" data-testid="ranking-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {POSITIONS.map(pos => (
                    <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 text-center text-muted-foreground text-sm">Carregando...</div>
            ) : filteredRankedAverages.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground text-sm">Nenhuma avaliação encontrada</div>
            ) : (
              <div className="divide-y divide-border/50">
                {filteredRankedAverages.map((player, index) => (
                  <div 
                    key={player.id}
                    className="flex items-center gap-2 md:gap-3 px-3 py-2 md:px-4 md:py-3 hover:bg-muted/40 transition-colors cursor-pointer"
                    onClick={() => navigate(`/players/${player.id}/history`)}
                    data-testid={`ranking-player-${index}`}
                  >
                    <div className="w-6 md:w-8 flex items-center justify-center flex-shrink-0">
                      {getRankIcon(index)}
                    </div>
                    
                    {player.photo_url ? (
                      <img 
                        src={getPhotoUrl(player.photo_url)}
                        alt={getPlayerFullName(player)}
                        className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover border-2 border-border flex-shrink-0"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-8 h-8 md:w-10 md:h-10 rounded-full items-center justify-center border-2 border-border flex-shrink-0 ${player.photo_url ? 'hidden' : 'flex'}`}
                      style={{ backgroundColor: getAvatarColor(getPlayerFullName(player)) }}
                    >
                      <span className="text-xs md:text-sm font-bold text-white drop-shadow">
                        {getInitials(player.first_name, player.last_name)}
                      </span>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground truncate text-xs md:text-sm">{getPlayerFullName(player)}</p>
                      <Badge variant="outline" className="text-[9px] md:text-[10px] border-border h-4 md:h-5">{player.position}</Badge>
                    </div>
                    
                    <div className="text-right flex-shrink-0">
                      <p className="text-base md:text-xl font-black text-green-500" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                        {player.average.toFixed(1)}
                      </p>
                      <p className="text-[9px] md:text-[10px] text-muted-foreground">{player.evaluations_count} aval.</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {filteredRankedAverages.length > 0 && (
              <div className="border-t border-border/50 p-2 text-center">
                <button
                  onClick={() => navigate('/ranking')}
                  className="text-xs text-primary hover:underline font-medium"
                  data-testid="see-full-ranking"
                >
                  Ver ranking completo
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Players Needing Evaluation */}
        <Card className="bg-card/80 border border-border rounded-xl">
          <CardHeader className="border-b border-border pb-3 px-4 md:px-5">
            <CardTitle className="text-sm md:text-lg font-bold uppercase tracking-tight flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              <Target className="h-4 w-4 text-[hsl(var(--highlight))]" />
              Precisam de Avaliação
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 text-center text-muted-foreground text-sm">Carregando...</div>
            ) : playersNeedingEval.players?.length === 0 ? (
              <div className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                  <Trophy className="h-6 w-6 text-green-400" />
                </div>
                <p className="text-green-400 font-bold text-sm">Todos avaliados!</p>
                <p className="text-xs text-muted-foreground mt-1">Nenhum jogador pendente</p>
              </div>
            ) : (
              <div className="divide-y divide-border/50">
                {playersNeedingEval.players?.slice(0, 8).map((player) => (
                  <div 
                    key={player.id}
                    className="flex items-center gap-2 px-3 py-2 md:px-4 md:py-3 hover:bg-muted/40 transition-colors cursor-pointer"
                    onClick={() => navigate('/evaluate')}
                    data-testid={`needs-eval-dash-${player.id}`}
                  >
                    {player.photo_url ? (
                      <img 
                        src={getPhotoUrl(player.photo_url)}
                        alt={getPlayerFullName(player)}
                        className="w-8 h-8 rounded-full object-cover border border-border flex-shrink-0"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-8 h-8 rounded-full items-center justify-center border border-border flex-shrink-0 ${player.photo_url ? 'hidden' : 'flex'}`}
                      style={{ backgroundColor: getAvatarColor(getPlayerFullName(player)) }}
                    >
                      <span className="text-xs font-bold text-white drop-shadow">
                        {getInitials(player.first_name, player.last_name)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate text-xs">{getPlayerFullName(player)}</p>
                      <Badge variant="outline" className="text-[9px] border-border h-4">{player.position}</Badge>
                    </div>
                    <Badge className="bg-[hsl(var(--highlight))] text-[hsl(var(--highlight-foreground))] border-0 text-[9px] md:text-[10px] font-semibold flex-shrink-0">
                      Pendente
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
